import { gql, useApolloClient } from "@apollo/client";
import { Icon, Text } from "@ttc3k/trekker";
import { EnumUserQuickLinksType } from "gql/generated";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Flex, styled } from "styled-system/jsx";
import { useNavigate } from "react-router";
import { Search, Building, Shop, ProfileCircle, Settings } from "iconoir-react";
import { useQuickLinks } from "apps/home/components";
import { SmartImage } from "shared/components";
import { WhereaboutsModal } from "../WhereaboutsModal";

type GlobalRouteSearchLauncherProps = {
  isOpen: boolean;
  onClose: () => void;
};

type RouteSearchResult = {
  targetId: string;
  kind: string;
  ownerType: string;
  ownerEntityId?: string | null;
  ownerLabel?: string | null;
  ownerShortName?: string | null;
  logoCloudinaryId?: string | null;
  label: string;
  path: string;
};

type GlobalRouteSearchQueryData = {
  globalRouteSearch: RouteSearchResult[];
};

type GlobalRouteSearchQueryVars = {
  query: string;
  limit: number;
};

const GLOBAL_ROUTE_SEARCH_QUERY = gql`
  query GlobalRouteSearch($query: String!, $limit: Int) {
    globalRouteSearch(query: $query, limit: $limit) {
      targetId
      kind
      ownerType
      ownerEntityId
      ownerLabel
      ownerShortName
      logoCloudinaryId
      label
      path
    }
  }
`;

const SEARCH_DEBOUNCE_MS = 100;
const SEARCH_LIMIT = 30;

const KIND_ICON_MAP: Record<
  string,
  React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >
> = {
  adminlandView: Settings,
  organizationRoute: Building,
  organizationApp: Building,
  organizationOperatorRoute: ProfileCircle,
  operatorRoute: ProfileCircle,
  storefrontRoute: Shop,
};

const KIND_LABEL_MAP: Record<string, string> = {
  adminlandView: "Adminland",
  organizationRoute: "Organization",
  organizationApp: "Organization",
  organizationOperatorRoute: "Operator",
  operatorRoute: "Operator",
  storefrontRoute: "Storefront",
};

export const GlobalRouteSearchLauncher = ({
  isOpen,
  onClose,
}: GlobalRouteSearchLauncherProps) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { addQuickLink } = useQuickLinks();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cacheRef = useRef(new Map<string, RouteSearchResult[]>());
  const requestIdRef = useRef(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RouteSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
      setIsLoading(false);
      return;
    }

    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSelectedIndex(0);
      setIsLoading(false);
      return;
    }

    const cached = cacheRef.current.get(trimmed);
    if (cached) {
      setResults(cached);
      setSelectedIndex(0);
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await client.query<
          GlobalRouteSearchQueryData,
          GlobalRouteSearchQueryVars
        >({
          query: GLOBAL_ROUTE_SEARCH_QUERY,
          variables: { query: trimmed, limit: SEARCH_LIMIT },
          fetchPolicy: "network-only",
        });

        if (requestId !== requestIdRef.current) return;

        const nextResults = response.data?.globalRouteSearch ?? [];
        cacheRef.current.set(trimmed, nextResults);
        setResults(nextResults);
        setSelectedIndex(0);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          setResults([]);
        }
        console.error(error);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [client, isOpen, query]);

  const selectedResult = useMemo(
    () => results[selectedIndex] ?? null,
    [results, selectedIndex],
  );

  const scrollToIndex = (index: number) => {
    const list = listRef.current;
    if (!list) return;
    const items = list.querySelectorAll("[data-result-item]");
    items[index]?.scrollIntoView({ block: "nearest" });
  };

  const handleSelect = (result: RouteSearchResult) => {
    const quickLink = getQuickLinkPayload(result);
    if (quickLink) {
      addQuickLink(
        quickLink.title,
        quickLink.link,
        quickLink.type,
        quickLink.entityId,
      );
    }
    onClose();
    navigate(result.path);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedIndex(prev => {
        const next = results.length === 0 ? 0 : Math.min(prev + 1, results.length - 1);
        scrollToIndex(next);
        return next;
      });
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex(prev => {
        const next = results.length === 0 ? 0 : Math.max(prev - 1, 0);
        scrollToIndex(next);
        return next;
      });
      return;
    }
    if (event.key === "Enter" && selectedResult) {
      event.preventDefault();
      handleSelect(selectedResult);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      title="Search Party!"
      description={`Discover Your Whereabouts`}
      size="md"
      customFooter={<div />}
      initialFocus={null}
    >
      <Flex flexDir="column" gap="200">
        <Flex
          alignItems="center"
          gap="100"
          bg="white"
          borderRadius="100"
          px="150"
          py="100"
          border="1px solid"
          borderColor="border.light"
        >
          <Icon Element={Search} size="sm" color="icon.light" />
          <styled.input
            ref={inputRef}
            type="text"
            placeholder="Search routes (Option+W / Alt+W)"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            bg="transparent"
            border="none"
            outline="none"
            fontSize="sm"
            color="text.mid"
            w="full"
            _placeholder={{ color: "text.lightest" }}
          />
        </Flex>

        <Flex
          ref={listRef}
          flexDir="column"
          gap="0"
          h="320px"
          overflowY="auto"
          bg="white"
          borderRadius="100"
          border="1px solid"
          borderColor="border.light"
        >
          {!query.trim() ? (
            <Flex h="full" alignItems="center" justifyContent="center">
              <Text fontSize="xs" color="text.lightest">
                Start typing to search internal routes
              </Text>
            </Flex>
          ) : isLoading && results.length === 0 ? (
            <Flex h="full" alignItems="center" justifyContent="center">
              <Text fontSize="xs" color="text.lightest">
                Searching...
              </Text>
            </Flex>
          ) : results.length === 0 && query.trim() ? (
            <Flex h="full" alignItems="center" justifyContent="center" px="150">
              <Text fontSize="xs" color="text.lightest">
                No matches. If new deploy, run navSearchIndex from Adminland.
              </Text>
            </Flex>
          ) : (
            results.map((result, index) => (
              <PickerItem
                key={result.targetId}
                data-result-item
                isSelected={index === selectedIndex}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => handleSelect(result)}
              >
                <ResultContent result={result} />
              </PickerItem>
            ))
          )}
        </Flex>
      </Flex>
    </WhereaboutsModal>
  );
};

const stripOrgPrefix = (label: string, kind: string) => {
  if (kind !== "organizationApp") return label;
  const parts = label.split(" - ");
  if (parts.length < 2) return label;
  const orgName = parts[0];
  const routeLabel = parts.slice(1).join(" - ").replace(/^Organization\s+/i, "");
  return `${orgName} - ${routeLabel}`;
};

const getQuickLinkPayload = (
  result: RouteSearchResult,
): {
  title: string;
  link: string;
  type: EnumUserQuickLinksType;
  entityId: string;
} | null => {
  switch (result.kind) {
    case "organizationRoute":
    case "organizationApp":
      return {
        title: result.ownerLabel ?? stripOrgPrefix(result.label, result.kind),
        link: result.path,
        type: EnumUserQuickLinksType.Organization,
        entityId: result.ownerEntityId ?? result.targetId,
      };
    case "organizationOperatorRoute":
    case "operatorRoute":
      return {
        title: result.label,
        link: result.path,
        type: EnumUserQuickLinksType.Operator,
        entityId: result.targetId,
      };
    default:
      return null;
  }
};

const ResultContent = ({ result }: { result: RouteSearchResult }) => {
  const IconElement = KIND_ICON_MAP[result.kind] ?? Settings;
  const kindLabel = KIND_LABEL_MAP[result.kind] ?? result.kind;
  const showLogo =
    result.logoCloudinaryId &&
    (result.kind === "organizationRoute" || result.kind === "organizationApp");
  const displayLabel = stripOrgPrefix(result.label, result.kind);

  return (
    <Flex alignItems="center" gap="150" w="full" minW={0}>
      <Flex flexDir="column" alignItems="center" gap="25" flexShrink={0} w="80px">
        {showLogo ? (
          <Flex w="32px" h="32px" borderRadius="4px" overflow="hidden">
            <SmartImage
              image={{ _id: result.logoCloudinaryId! }}
              width={32}
              shape="rounded"
              isLogo
            />
          </Flex>
        ) : (
          <Flex w="32px" h="32px" alignItems="center" justifyContent="center">
            <Icon Element={IconElement} size="sm" color="icon.light" />
          </Flex>
        )}
        <styled.span style={{ fontSize: "9px", lineHeight: 1 }} color="text.lightest" whiteSpace="nowrap">
          {kindLabel}
        </styled.span>
      </Flex>
      <Flex flexDir="column" gap="0" minW={0} flex={1}>
        <Text visual="smallMedium" color="text.dark" lineClamp={1} wordBreak="break-all">
          {displayLabel}
        </Text>
        <Text fontSize="3xs" color="text.lightest" lineClamp={1} wordBreak="break-all">
          {result.path}
        </Text>
      </Flex>
    </Flex>
  );
};

const PickerItem = styled("button", {
  base: {
    display: "flex",
    alignItems: "center",
    px: "150",
    py: "100",
    cursor: "pointer",
    bg: "white",
    border: "none",
    textAlign: "left",
    w: "full",
    borderRadius: "0",
    _hover: {
      bg: "bg.subtle",
    },
  },
  variants: {
    isSelected: {
      true: {
        bg: "bg.subtle",
      },
      false: {},
    },
  },
});
